/**
 * Client-side preview materialization: swap each slot's marked element for
 * the selected branch's content, per the branch-content contract (see
 * `@hightouch/lib/.../dynamic-content/shared.ts`): content is the whole
 * element (outerHTML, marker stripped), empty content means "this audience
 * sees nothing", and the marker is re-stamped onto the incoming content's
 * root so the slot stays locatable for the next toggle.
 *
 * Two shapes of the same swap:
 * - `materializeSlotSelections` — whole-html string transform, for building
 *   the document a preview iframe loads.
 * - `buildSelectionFragments` + `applySelectionFragments` — per-slot swap
 *   strings applied to the LIVE iframe document, so a variant toggle changes
 *   one element in place instead of reloading the whole email.
 *
 * Both render empty content as a hidden, marker-carrying placeholder rather
 * than removing the element — removal would be a one-way door for the live
 * document (nothing left to find on the next toggle).
 *
 * The server counterpart is `materializeSlotBranches`
 * (`packages/backend/lib/llm/lifecycle-studio/dynamic-content/materialize.ts`),
 * which splices the source string to stay byte-exact. It can't be imported
 * here (cheerio; `shared.ts` is the deliberate browser-safe boundary), so this
 * version drives the same contract through the DOM instead — the idiom the
 * editor already uses for version writes (`use-slot-lifecycle.ts`). Output is
 * preview-only and never persisted.
 */
import {
  DYNAMIC_SLOT_ATTRIBUTE,
  effectiveSelection,
  stampMarkerOnRootTag,
} from "@hightouch/lib/llm/lifecycle-studio/dynamic-content/shared";

import { findSlotElement, type DynamicSlot } from "./types";
import type { SlotSelections } from "src/pages/lifecycle-studio/dynamic-content/slot-selections";

// These moved to the shared dynamic-content domain (the message preview needs
// them too); re-exported so email's existing imports keep resolving here.
export {
  combinationCount,
  variantSelectionBranchId,
  type SlotSelections,
} from "src/pages/lifecycle-studio/dynamic-content/slot-selections";

/** slotId → swap string for the live document: the stamped branch element,
 * or "" for empty content (hide the element). Slots whose content can't be
 * stamped are omitted — their baked element stays. */
export type SelectionFragments = Record<string, string>;

export type FragmentOptions = {
  /** Runs on each stamped fragment (the preview uses it for the client
   * personalization render) so what lands in the document matches a full
   * materialize+render. */
  renderHtml?: (html: string) => string;
  /** What EMPTY content ("this audience sees nothing") renders as: a hidden
   * placeholder, or no fragment at all so the baked element stays. The canvas
   * needs the latter — a hidden placeholder has no box, and a slot's lane,
   * connector and handle all anchor off that box. */
  onEmpty?: "hide" | "keepBaked";
};

/** Per-slot swap strings for the current selections. */
export function buildSelectionFragments(
  slots: DynamicSlot[],
  selections: SlotSelections,
  { renderHtml = (html) => html, onEmpty = "hide" }: FragmentOptions = {},
): SelectionFragments {
  const fragments: SelectionFragments = {};
  for (const slot of slots) {
    const branch = effectiveSelection(slot, selections);
    if (!branch) continue;
    const stamped = stampMarkerOnRootTag(branch.content, slot.id);
    // No stampable root (legacy/malformed content) — leave the baked element.
    if (stamped === null) continue;
    if (stamped === "") {
      if (onEmpty === "keepBaked") continue;
      fragments[slot.id] = "";
      continue;
    }
    fragments[slot.id] = renderHtml(stamped);
  }
  return fragments;
}

/** "This audience sees nothing", reversibly: same tag (so the swap is valid
 * wherever the element sits, e.g. a td), marker kept, display suppressed. */
function hideElement(element: Element): void {
  const placeholder = element.ownerDocument.createElement(element.tagName);
  const slotId = element.getAttribute(DYNAMIC_SLOT_ATTRIBUTE);
  if (slotId) placeholder.setAttribute(DYNAMIC_SLOT_ATTRIBUTE, slotId);
  placeholder.setAttribute("style", "display:none");
  element.replaceWith(placeholder);
}

/**
 * Swap fragments into a live document, in place. Returns the fragments that
 * were actually applied so callers can skip unchanged slots on the next call
 * (swapping identical content still resets that element's rendering).
 * Missing markers are tolerated (mid-generation / stale rows — same as the
 * dynamic-content canvas).
 */
export function applySelectionFragments(
  doc: Document,
  fragments: SelectionFragments,
  alreadyApplied: SelectionFragments = {},
): SelectionFragments {
  const applied: SelectionFragments = { ...alreadyApplied };
  for (const [slotId, fragment] of Object.entries(fragments)) {
    if (applied[slotId] === fragment) continue;
    const element = findSlotElement(doc, slotId);
    if (!element) continue;
    if (fragment === "") {
      hideElement(element);
    } else {
      // outerHTML assignment parses in the parent's context, so table
      // fragments (td/tr slots) land as their real tags.
      element.outerHTML = fragment;
    }
    applied[slotId] = fragment;
  }
  return applied;
}

/**
 * What the dynamic-content canvas displays as "the email": the session html
 * with each slot's default branch (first in waterfall order, the one an export
 * defaults to) swapped in for the baked element. Once a slot exists its branch
 * rows own the content, so the baked element is stale.
 *
 * Slots whose default content is EMPTY keep their baked element (`onEmpty:
 * "keepBaked"` — see `FragmentOptions`).
 *
 * Display only — `restoreBakedSlotElements` is the inverse.
 */
export function materializeFirstVariantsForDisplay(
  html: string,
  slots: DynamicSlot[],
): string {
  return materializeSlotSelections(html, slots, {}, { onEmpty: "keepBaked" });
}

/** Per-slot displayed content as one comparable string. The canvas watches it
 * to reload when that content actually moves, not on refetch identity churn. */
export function firstVariantsDisplayKey(slots: DynamicSlot[]): string {
  return JSON.stringify(
    slots.map((slot) => [
      slot.id,
      effectiveSelection(slot, undefined)?.content ?? "",
    ]),
  );
}

/**
 * Inverse of the display materialize, in place: swap each marked element back
 * to its baked outerHTML (`bakedBySlotId`, the shape `collectSlotElementHtml`
 * returns, marker included). Slot elements are locked in the canvas session, so
 * the baked element stays correct wherever a block move carried the marker.
 * Markers with no baked entry (orphans, mid-generation rows) are left alone.
 */
export function restoreBakedSlotElementsInDoc(
  doc: Document,
  bakedBySlotId: Record<string, string>,
): boolean {
  let swapped = false;
  for (const [slotId, baked] of Object.entries(bakedBySlotId)) {
    const element = findSlotElement(doc, slotId);
    if (!element) continue;
    element.outerHTML = baked;
    swapped = true;
  }
  return swapped;
}

/**
 * String shape of the swap-back. Returns the input unchanged when nothing was
 * restored, and preserves whether it carried a doctype (captures are
 * `documentElement.outerHTML`, so doctype-less).
 */
export function restoreBakedSlotElements(
  html: string,
  bakedBySlotId: Record<string, string>,
): string {
  if (Object.keys(bakedBySlotId).length === 0) return html;
  const doc = new DOMParser().parseFromString(html, "text/html");
  if (!restoreBakedSlotElementsInDoc(doc, bakedBySlotId)) return html;
  const doctype = /^\s*<!doctype/i.test(html) ? "<!DOCTYPE html>" : "";
  return `${doctype}${doc.documentElement.outerHTML}`;
}

/**
 * Whole-html swap: parse, apply every selection, serialize. For building the
 * document a preview loads (the live document is then kept current with
 * `applySelectionFragments`). Returns the input unchanged when nothing
 * applied, so callers diffing the string don't see a no-op round-trip.
 */
export function materializeSlotSelections(
  html: string,
  slots: DynamicSlot[],
  selections: SlotSelections,
  options?: FragmentOptions,
): string {
  if (slots.length === 0) return html;

  const doc = new DOMParser().parseFromString(html, "text/html");
  const applied = applySelectionFragments(
    doc,
    buildSelectionFragments(slots, selections, options),
  );

  if (Object.keys(applied).length === 0) return html;
  // Same serialization the editor uses when writing versions from a parsed
  // doc (use-slot-lifecycle). Not byte-exact, but this string only ever feeds
  // the preview iframe and the client-side personalization render.
  return `<!DOCTYPE html>${doc.documentElement.outerHTML}`;
}
